<template>
  <mailbox-verification-layout :name="mailboxName" show-cancel-option show-start-over-option>
    <el-card>
      <template #header>
        <h3>Verifying your auto-forwarding setup</h3>
      </template>
      <entity-settings-loading v-if="isLoading" />
      <div v-else class="mailbox-verification-auto-forwarding-testing">
        <template v-if="pollingForAutoForwardingTest">
          <p>
            We're now testing that emails sent to <strong>{{ forwardingFromAddress }}</strong> automatically
            forward to this mailbox.
          </p>
          <p><br></p>
          <p>
            This could take a couple of minutes, so now's a good time to grab that second cup of coffee.
            <strong>Don't refresh this screen!</strong>
          </p>
          <verification-loader />
        </template>
        <template v-else>
          <div v-if="autoForwardingConfirmed">
            <p>Confirmed! Emails sent to <strong>{{ forwardingFromAddress }}</strong> are auto-forwarding to this mailbox. 
            </p>
            <img :src="randomGiphyUrl" class="giphy-applause">
            <div v-if="hasCustomFromAddress" class="from-address-options">
              <dd-button 
                :color="ColorAssistant.MailboxColor"
                size="large" 
                @click="goToMailboxVerificationFromAddress()">Set this as your outgoing email address</dd-button>
              <dd-button
                size="large"
                type="text"
                @click="goToMailbox()">Or, just go back to your mailbox</dd-button>
            </div>
            <dd-button
              v-else
              :color="ColorAssistant.MailboxColor"
              full-width
              size="large"
              @click="goToMailboxVerificationFromAddress()">Last step! Set up your outgoing email address...</dd-button>
          </div>
          <div v-else>
            <p>
              Hmm. We can't confirm auto-forwarding is working. Try these suggestions:
            </p>
            <p><br></p>
            <ul>
              <li>
                <strong><a href="#" class="just-a-link" @click.stop.prevent="sendAndPollAutoForwarding()">Try it again</a></strong>. It may be that
                your email provider's auto-forwarding rule takes a minute to set.
              </li>
              <li>
                <a href="#" class="just-a-link" @click.stop.prevent="RouteAssistant.goToMailboxVerificationAutoForwardingRoute($router, accountID, mailboxID)">
                  Go back to the auto-forwarding steps</a> and review the instructions again.</li>
              <li>At a loss? <a href="https://www.donedone.com/help-center" target="_blank" class="just-a-link">Reach out to us</a> so we can help.</li>
            </ul>
          </div>
        </template>
      </div>
    </el-card>
  </mailbox-verification-layout>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'
import MailboxVerificationLayout from '@/layouts/mailbox-verification-layout.vue'

/* Mixins */

/* Components */
import EntitySettingsLoading from '@/layouts/entity-settings-loading.vue'

/* Helpers */
import RouteAssistant from '@/helpers/route-assistant'
import ColorAssistant from '@/helpers/color-assistant'
import LocalStore from '@/helpers/localstore'

/* Data types */
import { IMailboxesOutputAutoForwarding } from '@/api/types/response.types'

/* API */
import MailboxesAPI from '@/api/modules/mailboxes.api'

export default defineComponent({

  components: {
    MailboxVerificationLayout,
    EntitySettingsLoading,
  },

  data() {
    return {
      RouteAssistant,
      ColorAssistant,
      LocalStore,
      mailboxName: '',
      isLoading: true,
      pollingForAutoForwardingTest: false,
      autoForwardingConfirmed: false,
      poller: null as any,
      pollCount: 0,
      pollIntervalInMS: 5000,
      pollMax: 36,
      autoForwardingSettings: {} as IMailboxesOutputAutoForwarding,
      /*
      Will only be true for folks who've done complete setup, but come back to do another round of auto-forwarding with
      a new email address. This lets them skip the "From" address setup part 
      */
      hasCustomFromAddress: false,
    }
  },
  head: {
    title: 'Verify Auto-Forwarding Address',
  },

  computed: {
    accountID(): number {
      return Number(this.$route.params.accountId)
    },
    mailboxID(): number {
      return Number(this.$route.params.id)
    },
    forwardingFromAddress(): string {
      return LocalStore.mailboxForwardingAddressCandidate
    },
    randomGiphyUrl(): string {
      const urls = [
        'https://media.giphy.com/media/143WAqb351Q1Vu/giphy.gif',
        'https://media.giphy.com/media/2aPePPqpAf5jwjtt2p/giphy.gif',
        'https://media.giphy.com/media/26uf6o80xhd6MKGIw/giphy.gif',
      ]

      return urls[Math.floor(Math.random() * urls.length)]
    },
  },

  watch: {
    pollCount: {
      handler(newValue) {
        if (newValue >= this.pollMax) {
          this.endPolling()
        }
      },
    },
  },

  async created() {
    this.isLoading = true
    await this.getAutoForwardingInfo()
    await this.sendAndPollAutoForwarding()
    this.isLoading = false
  },

  beforeUnmount() {
    this.endPolling()
  },

  methods: {
    endPolling() {
      clearInterval(this.poller)
      this.pollingForAutoForwardingTest = false
    },
    async sendAndPollAutoForwarding() {
      await this.sendTestAutoForwardingEmail()
      await this.pollForAutoForwardingConfirmation()
    },
    async pollForAutoForwardingConfirmation() {
      this.pollCount = 0
      this.poller = setInterval(async () => {
        this.pollCount++
        await this.confirmAutoForwardingTest()
      }, this.pollIntervalInMS)
      this.pollingForAutoForwardingTest = true
    },
    async getAutoForwardingInfo() {
      try {
        this.autoForwardingSettings = await MailboxesAPI.get_AutoForwarding(
          this.mailboxID,
        )
        this.mailboxName = this.autoForwardingSettings.name
      } catch (error) {
        this.$error.emit(error)
      }
    },
    async confirmAutoForwardingTest() {
      try {
        const response = await MailboxesAPI.get_ConfirmAutoForwardingTestEmail(
          this.mailboxID,
        )
        if (response.verified) {
          LocalStore.clearMailboxForwardingAddressCandidate()
          await this.setHasCustomFromAddress()
          this.autoForwardingConfirmed = true
          this.endPolling()
        }
      } catch (error) {
        this.$error.emit(error, true)
        this.endPolling()
      }
    },
    async setHasCustomFromAddress() {
      try {
        const response = await MailboxesAPI.get_OutgoingEmailSettings(
          this.mailboxID,
        )
        this.hasCustomFromAddress = response.dnsInfo !== null
      } catch (error) {
        this.$error.emit(error, true)
        this.endPolling()
      }
    },
    async sendTestAutoForwardingEmail() {
      try {
        await MailboxesAPI.post_SendTestEmailToAutoForwardingAddress(
          this.mailboxID,
          this.forwardingFromAddress,
        )
      } catch (error) {
        this.$error.emit(error)
      }
    },
    async goToMailbox() {
      RouteAssistant.goToMailbox(this.$router, this.accountID, this.mailboxID)
    },
    async goToMailboxVerificationFromAddress() {
      RouteAssistant.goToMailboxVerificationOutgoingEmailRoute(
        this.$router,
        this.accountID,
        this.mailboxID,
      )
    },
  },
})
</script>

<style lang="scss">
.mailbox-verification-auto-forwarding-testing {
  .giphy-applause {
    border-radius: 8px;
    display: block;
    margin: 24px auto;
    opacity: 0.9;
  }

  .from-address-options {
    display: flex;
    flex-direction: column;
  }
}
</style>
